import { io, Socket } from 'socket.io-client';
import { supabase } from './supabase';
import { WS_URL } from '../config/env';

/**
 * Canonical frontend Socket.IO client.
 * Cookie auth (withCredentials) + Bearer handshake token for mobile/web parity.
 * SocketContext and hooks/useSocket both use this singleton.
 */
let socket: Socket | null = null;
const socketsEnabled = import.meta.env.VITE_ENABLE_SOCKETS !== 'false';

export const initializeSocket = async (): Promise<Socket | null> => {
  if (!socketsEnabled) {
    if (import.meta.env.DEV) {
      console.log('[WS] Socket connections disabled via environment flag');
    }
    return null;
  }

  if (!socket) {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    socket = io(WS_URL, {
      auth: {
        token: session?.access_token || null,
      },
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 5000,
      transports: ['polling', 'websocket'],
      upgrade: true,
    });

    socket.on('connect', () => {
      if (import.meta.env.DEV) console.log('[WS] Socket connected, id:', socket?.id);
    });

    socket.on('disconnect', (reason) => {
      if (import.meta.env.DEV) console.log('[WS] Socket disconnected, reason:', reason);
    });

    socket.on('connect_error', (error) => {
      if (import.meta.env.DEV) console.error('[WS] Socket connection error:', error.message);
    });

    socket.on('error', (error) => {
      if (import.meta.env.DEV) console.error('[WS] Socket error:', error);
    });

    if (import.meta.env.DEV) {
      socket.onAny((eventName, ...args) => {
        if (eventName.includes('location') || eventName.includes('guest')) {
          console.log('[WS] Received event:', eventName, args);
        }
      });
    }
  }
  return socket;
};

export const checkWsStatus = async (): Promise<void> => {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) {
      if (import.meta.env.DEV) console.log('[WS Debug] Not authenticated');
      return;
    }

    const apiUrl = WS_URL.replace(/^wss?:\/\//, 'https://');
    const response = await fetch(`${apiUrl}/v1/health/ws-status`, {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
      credentials: 'include',
    });

    if (import.meta.env.DEV) {
      const data = await response.json();
      console.log('[WS Debug] Server WebSocket status:', data);
      console.log('[WS Debug] Local socket state:', {
        socketExists: !!socket,
        connected: socket?.connected,
        socketId: socket?.id,
      });
    }
  } catch (error) {
    if (import.meta.env.DEV) console.error('[WS Debug] Error checking status:', error);
  }
};

if (typeof window !== 'undefined') {
  (window as unknown as { checkWsStatus: typeof checkWsStatus }).checkWsStatus = checkWsStatus;
}

export const getSocket = async (): Promise<Socket | null> => {
  if (!socketsEnabled) {
    return null;
  }
  if (!socket) {
    return await initializeSocket();
  }
  return socket;
};

export const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};
